import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { TeachingPath, TeachingPathsList } from 'teachingPath/TeachingPath';
import { USER_SERVICE, UserService } from 'user/UserService';
import { injector } from 'Injector';

import { debounce } from 'utils/debounce';
import { DEBOUNCE_TIME, postperpage } from 'utils/constants';
import { Filter } from 'assignment/Assignment';
import { UserType } from 'user/User';
import { SortingFilter, StoreState } from 'utils/enums';
import { Notification, NotificationTypes } from '../../../components/common/Notification/Notification';
import intl from 'react-intl-universal';
import { reportFailure } from 'utils/reportFailure';
import { messageOf } from 'utils/apiError';
const TEACHING_PATHS_PER_PAGE_IN_LIST = postperpage;

export class TeachingPathsListStore {

  private userService: UserService = injector.get(USER_SERVICE);
  private listTeachingPaths = new TeachingPathsList();

  @observable public teachingPathsState: StoreState = StoreState.PENDING;
  @observable public typeOfTeachingPathsList: string = 'all';

  @observable public paginationTotalPages: number = 1;
  @observable public teachingPathList: Array<TeachingPath> = [];
  @observable public currentTeachingPath?: TeachingPath;
  public teachingPathsForSkeleton: Array<TeachingPath> = new Array<TeachingPath>(TEACHING_PATHS_PER_PAGE_IN_LIST).fill(new TeachingPath({ id: 0, title: '' }));
  public currentEntityTypeRoute: string = 'teaching-path';

  // Through an arrow, so `this` is the store however the wrapper is called, and the method is
  // read when it runs, after makeObservable has wrapped it as an action.
  public debounceWrapper = debounce(() => { void this.getTeachingPathsList(); }, DEBOUNCE_TIME);

  constructor() {
    makeObservable(this);
  }

  @computed
  public get teachingPathsList() {
    return this.teachingPathList;
  }

  @action
  public async getTeachingPathsList() {
    this.teachingPathsState = StoreState.LOADING;
    try {
      this.listTeachingPaths.setFiltersPerPage(TEACHING_PATHS_PER_PAGE_IN_LIST);
      let response;
      if (this.userService.getCurrentUser()!.type === UserType.Student) {
        response = await this.listTeachingPaths.getStudentTeachingPathsList();
      } else {
        switch (this.typeOfTeachingPathsList) {
          case 'all':
            this.listTeachingPaths.setFilterSchoolTeachingPath(0);
            this.listTeachingPaths.setFilterInReviewTeachingPath(false);
            response =  await this.listTeachingPaths.getAllTeachingPathsList();
            break;
          case 'my':
            this.listTeachingPaths.setFilterSchoolTeachingPath(0);
            this.listTeachingPaths.setFilterInReviewTeachingPath(false);
            response =  await this.listTeachingPaths.getMyTeachingPathsList();
            break;
          case 'inreview':
            this.listTeachingPaths.setFilterSchoolTeachingPath(0);
            this.listTeachingPaths.setFilterInReviewTeachingPath(true);
            response =  await this.listTeachingPaths.getInReviewTeachingPathsList();
            break;
          case 'myschool':
            this.listTeachingPaths.setFilterSchoolTeachingPath(1);
            this.listTeachingPaths.setFilterInReviewTeachingPath(false);
            response =  await this.listTeachingPaths.getMySchoolTeachingPathsList();
            break;
          default:
            this.listTeachingPaths.setFilterSchoolTeachingPath(0);
            response =  await this.listTeachingPaths.getAllTeachingPathsList();
            break;
        }
      }

      runInAction(() => {
        this.teachingPathList = response.teachingPathsList;
        this.paginationTotalPages = response.total_pages;
        this.teachingPathsState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.teachingPathsState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  @action
  public async getTeachingPathDistributeList(filter: Filter) {
    this.teachingPathsState = StoreState.LOADING;
    try {
      this.listTeachingPaths.setFiltersPerPage(TEACHING_PATHS_PER_PAGE_IN_LIST);
      const response = await this.listTeachingPaths.getTeachingPathDistributes(filter);

      runInAction(() => {
        this.teachingPathList = response.teachingPathsList;
        this.paginationTotalPages = response.total_pages;
        this.teachingPathsState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.teachingPathsState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  @action
  public async getTeachingPathListOfStudentInList(studentId: number, _filter: Filter) {
    this.teachingPathsState = StoreState.LOADING;
    try {
      const response = await this.listTeachingPaths.getTeachingPathListOfStudentInList(studentId);

      runInAction(() => {
        this.teachingPathList = response.teachingPathsList;
        this.paginationTotalPages = response.total_pages;
        this.teachingPathsState = StoreState.PENDING;
      });
    } catch (error) {
      runInAction(() => { this.teachingPathsState = StoreState.ERROR; });
      reportFailure(error);
    }
  }

  @action
  public async copyEntity(id: number): Promise<number> {
    try {
      const copyId = await this.listTeachingPaths.copyTeachingPath(id);
      Notification.create({
        type: NotificationTypes.SUCCESS,
        title: intl.get('teaching_paths_list.copy successfully')
      });
      return copyId;
    } catch (e) {
      throw new Error(`copy teaching path ${messageOf(e)}`, { cause: e });
    }
  }

  @action
  public setTypeOfTeachingPathsList(type: string) {
    this.typeOfTeachingPathsList = type;
  }

  @action
  public async setFiltersPage(number: number) {
    this.listTeachingPaths.setFiltersPage(number);
    await this.getTeachingPathsList();
  }

  public getCurrentUser = () => this.userService.getCurrentUser();

  @computed
  public get filter() {
    return this.listTeachingPaths.filter;
  }

  @computed
  public get currentPage() {
    return this.listTeachingPaths.filter.page;
  }

  @computed
  public get currentEntity() {
    return this.currentTeachingPath!;
  }

  @action
  public setCurrentTeachingPath(id: number) {
    this.currentTeachingPath = this.teachingPathList.find(item => item.id === id)!;
  }

  @action
  public setCurrentTeachingPathEntity(teachingPath: TeachingPath) {
    this.currentTeachingPath = teachingPath!;
  }

  public hasAssignment(id: number) {
    return !!this.teachingPathList.find(item => item.id === id);
  }

  public resetFilters = () => {
    this.listTeachingPaths.setFiltersGradeID(null);
    this.listTeachingPaths.setFiltersSubjectID(null);
    this.listTeachingPaths.setFiltersSearchQuery('');
    this.listTeachingPaths.setFilterSorting(SortingFilter.CREATION_DATE, SortingFilter.DESC);
  }

  public resetCurrentPage = () => {
    this.listTeachingPaths.setFiltersPage(1);
  }

  @action
  public resetTeachingPathsList = () => {
    this.resetFilters();
    this.resetCurrentPage();
    this.teachingPathList = [];
  }

  @action
  public getTeachingPathDataById = async (id: number) => {
    this.currentTeachingPath = await this.listTeachingPaths.getTeachingPathDataById(id);
  }

  public setFiltersGradeID(gradeID: number | null) {
    this.listTeachingPaths.setFiltersGradeID(gradeID);
    this.listTeachingPaths.setFiltersPage(1);
    this.getTeachingPathsList();
  }

  public setFiltersSubjectID(subjectID: number | null) {
    this.listTeachingPaths.setFiltersSubjectID(subjectID);
    this.listTeachingPaths.setFiltersPage(1);
    this.getTeachingPathsList();
  }

  public setFiltersSearchQuery(searchQuery: string) {
    this.listTeachingPaths.setFiltersSearchQuery(searchQuery);
    this.listTeachingPaths.setFiltersPage(1);
    this.debounceWrapper();
  }

  public get orderFieldFilterValue() {
    return this.listTeachingPaths.filter.searchQuery;
  }
}
